import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  COACH_OUTREACH_REPLIES_OPENED_EVENT,
  countUnreadCoachOutreachReplies,
  getCoachOutreachRepliesLastOpenedAt,
  setCoachOutreachRepliesLastOpenedAt,
  type CoachOutreachReplyForUnread,
} from '@/lib/coachOutreachRepliesStorage';

export function useCoachOutreachUnreadReplies(
  replies: CoachOutreachReplyForUnread[] | undefined,
) {
  const [lastOpenedAt, setLastOpenedAt] = useState<string | null>(() =>
    getCoachOutreachRepliesLastOpenedAt(),
  );

  useEffect(() => {
    const onOpened = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      setLastOpenedAt(detail ?? getCoachOutreachRepliesLastOpenedAt());
    };
    window.addEventListener(COACH_OUTREACH_REPLIES_OPENED_EVENT, onOpened);
    return () => {
      window.removeEventListener(COACH_OUTREACH_REPLIES_OPENED_EVENT, onOpened);
    };
  }, []);

  const unreadCount = useMemo(
    () => countUnreadCoachOutreachReplies(replies ?? [], lastOpenedAt),
    [replies, lastOpenedAt],
  );

  const markRepliesOpened = useCallback(() => {
    const at = new Date().toISOString();
    setCoachOutreachRepliesLastOpenedAt(at);
    setLastOpenedAt(at);
  }, []);

  return {
    unreadCount,
    hasUnread: unreadCount > 0,
    lastOpenedAt,
    markRepliesOpened,
  };
}
